import axios from "axios";
import { ApiResponse } from "../../types";
// Removed getAuthToken import - we'll handle token directly like apiV2Service
import {
  V2Message,
  V2CreateConversationRequest,
  V2CreateConversationResponse,
  V2SendMessageRequest,
  V2SendMessageResponse,
  V2UpdateMessageRequest,
  V2MarkMessagesReadRequest,
  V2ReportMessageRequest,
  V2ConversationsResponse,
  V2MessagesResponse,
} from "../../types/v2types";

import { Constants } from "../../utils/constants";
const CHAT_SERVICE_BASE_URL = Constants.CHAT_API_BASE_URL;
const MESSAGING_SERVICE_BASE_URL = Constants.MESSAGING_API_BASE_URL;

class MessagingService {
  private api;
  private messagingApi;

  constructor() {
    this.api = axios.create({
      baseURL: CHAT_SERVICE_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.messagingApi = axios.create({
      baseURL: MESSAGING_SERVICE_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add auth token interceptor with fallback like apiV2Service
    const authInterceptor = (config: any) => {
      let token = localStorage.getItem("authToken");
      if (!token) {
        token = localStorage.getItem("access_token");
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    };

    this.api.interceptors.request.use(authInterceptor);
    this.messagingApi.interceptors.request.use(authInterceptor);
  }

  // List conversations for the current user
  public async listConversations(): Promise<ApiResponse<V2ConversationsResponse>> {
    return this.api.get("/conversations");
  }

  // Create a new conversation
  public async createConversation(
    payload: V2CreateConversationRequest
  ): Promise<ApiResponse<V2CreateConversationResponse>> {
    return this.api.post("/conversations", payload);
  }

  // Get messages for a conversation
  public async getMessages(conversationId: string | number): Promise<ApiResponse<V2MessagesResponse>> {
    return this.api.get(`/messages`, { params: { conversationId } });
  }

  // Send a message to a conversation
  public async sendMessage(payload: V2SendMessageRequest): Promise<ApiResponse<V2SendMessageResponse>> {
    return this.api.post("/messages", payload);
  }

  // Update a message
  public async updateMessage(
    messageId: string | number,
    payload: V2UpdateMessageRequest
  ): Promise<ApiResponse<V2Message>> {
    return this.api.patch(`/messages/${messageId}`, payload);
  }

  // View a single message
  public async getMessage(messageId: string | number): Promise<ApiResponse<V2Message>> {
    return this.api.get(`/messages/${messageId}`);
  }

  // Delete a message
  public async deleteMessage(messageId: string | number): Promise<ApiResponse<void>> {
    return this.api.delete(`/messages/${messageId}`);
  }

  // Mark messages as read
  public async markMessagesAsRead(payload: V2MarkMessagesReadRequest): Promise<ApiResponse<void>> {
    return this.api.post("/messages/mark-read", payload);
  }

  // Mark messages status (read/unread) - DEPRECATED: use markMessagesAsRead instead
  public async markMessagesStatus(payload: { messageIds: number[]; isRead: boolean }): Promise<ApiResponse<void>> {
    return this.api.post("/messages-status", payload);
  }

  // Report a message
  public async reportMessage(messageId: string | number, payload: V2ReportMessageRequest): Promise<ApiResponse<void>> {
    return this.api.post(`/messages/${messageId}/report`, payload);
  }

  // Send message events (presence ping / typing heartbeat)
  public async sendMessageEvent(conversationId?: number): Promise<ApiResponse<void>> {
    const payload = conversationId ? { conversationId } : {};
    return this.api.post("/messages/events", payload);
  }

  // Register FCM token in the Messaging Service
  public async registerFCMToken(payload: {
    deviceId: string;
    platform: string;
    token: string;
  }): Promise<ApiResponse<void>> {
    return this.messagingApi.post("/api/v1/fcm-tokens", payload);
  }

  // Delete a conversation
  public async deleteConversation(conversationId: string | number): Promise<ApiResponse<void>> {
    return this.api.delete(`/conversations/${conversationId}`);
  }
}

export const messagingService = new MessagingService();
export default messagingService;
